import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLivePoll } from "../lib/useLivePoll";
import { Sparkles, AlertTriangle, Lock } from "lucide-react";
import { SILK_COLOURS } from "../lib/constants";
import Skeleton from "../components/Skeleton";

function ScoreBreakdown({ pick, race }) {
  const res = race.result;
  const wildcard = pick.wildcard_applied;
  const fmt = (n) => (n == null ? "0.00" : Number(n).toFixed(2));
  let line = null;
  let colour = "text-[#212529]";

  if (pick.is_win) {
    line = <>1st · Win ${fmt(res.win_dividend)} + Place ${fmt(res.place_dividend)} = <b>${fmt(res.win_dividend + res.place_dividend)} pts</b></>;
    colour = "text-[#c07800]";
  } else if (pick.horse_number === res.second_horse_number) {
    line = <>2nd · dividend ${fmt(res.second_dividend)} = <b>${fmt(res.second_dividend)} pts</b></>;
    colour = "text-[#c07800]";
  } else if (pick.horse_number === res.third_horse_number) {
    if (pick.no_third_dividend) {
      line = <>3rd · <b>No 3rd Dividend — 0 pts</b></>;
      colour = "text-[#c92a2a]";
    } else {
      line = <>3rd · dividend ${fmt(res.third_dividend)} = <b>${fmt(res.third_dividend)} pts</b></>;
      colour = "text-[#c07800]";
    }
  } else {
    line = <>Unplaced · <b>0 pts</b></>;
    colour = "text-[#6c757d]";
  }

  return (
    <div className="mt-3 rounded-xl bg-[#f8f9fa] border border-[#e9ecef] p-3 text-xs font-semibold space-y-1" data-testid={`score-breakdown-${race.id}`}>
      <div className={colour}>{line}</div>
      {wildcard && (
        <div className="text-[#6741d9] font-black">
          ×2 wildcard = <span className="text-lg tabular-nums">${fmt(pick.points_earned)} pts</span>
        </div>
      )}
    </div>
  );
}

export default function MyPicksTab({ group }) {
  const { user } = useAuth();
  const [dates, setDates] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [races, setRaces] = useState([]);
  const [picks, setPicks] = useState([]);
  const [meta, setMeta] = useState({ wildcards_allowance: 0, wildcards_used: 0, wildcards_remaining: 0 });
  const [loading, setLoading] = useState(true);

  const loadDates = useCallback(async () => {
    if (!group) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/meeting-dates`);
      setDates(data.meeting_dates);
      setActiveDate((cur) => {
        if (cur && data.meeting_dates.find((d) => d.meeting_date === cur)) return cur;
        const upcoming = data.meeting_dates.find((d) => !d.cutoff_passed);
        return (upcoming || data.meeting_dates[data.meeting_dates.length - 1])?.meeting_date || null;
      });
    } catch (e) { /* ignore */ }
  }, [group]);

  const loadData = useCallback(async () => {
    if (!group || !activeDate) { setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data: rd }, { data: pd }, { data: lb }] = await Promise.all([
        api.get(`/groups/${group.id}/races`, { params: { date: activeDate } }),
        api.get(`/groups/${group.id}/picks/me`, { params: { date: activeDate } }),
        api.get(`/groups/${group.id}/leaderboard`, { params: { view: "season" } }).catch(() => ({ data: null })),
      ]);
      setRaces(rd.races);
      setPicks(pd.picks || []);
      const me = lb?.data ? null : null;
      let rank = null, total = 0, wins = 0, places = 0, unplaced = 0;
      if (lb) {
        const mine = lb.entries.find((e) => e.user_id === user.id);
        if (mine) { rank = mine.rank; total = mine.total_points; wins = mine.wins; places = mine.places; unplaced = mine.unplaced; }
      }
      setMeta({
        wildcards_allowance: pd.wildcards_allowance,
        wildcards_used: pd.wildcards_used,
        wildcards_remaining: pd.wildcards_remaining,
        total_points: total, rank, wins, places, unplaced,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [group, activeDate, user]);

  useEffect(() => { loadDates(); }, [loadDates]);
  useEffect(() => { loadData(); }, [loadData]);
  useLivePoll(loadData, 15000, [group?.id, activeDate], { immediate: false });

  const raceById = useMemo(() => {
    const m = new Map();
    races.forEach((r) => m.set(r.id, r));
    return m;
  }, [races]);

  const toggleWildcard = async (pick) => {
    const race = raceById.get(pick.race_id);
    if (!race) return;
    if (race.status === "finished") return;
    const desired = !pick.wildcard_applied;
    try {
      const { data } = await api.patch(
        `/groups/${group.id}/races/${pick.race_id}/picks/wildcard`,
        { wildcard_applied: desired },
      );
      setPicks((prev) => prev.map((p) => (p.id === data.pick.id ? data.pick : p)));
      setMeta((prev) => ({
        ...prev,
        wildcards_used: prev.wildcards_used + (desired ? 1 : -1),
        wildcards_remaining: prev.wildcards_remaining + (desired ? -1 : 1),
      }));
      toast.success(desired ? "Wildcard applied" : "Wildcard removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Wildcard failed"));
    }
  };

  if (!group) return null;

  return (
    <div className="px-4 pt-4 space-y-4 pb-24" data-testid="my-picks-tab-content">
      {/* Summary card */}
      <div className="bg-white border border-[#e9ecef] rounded-2xl p-4 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-[#f8f9fa] border border-[#e9ecef] flex items-center justify-center text-3xl">
          {user?.avatar_emoji || "🐎"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold text-lg truncate">{user?.name}</div>
          <div className="text-[11px] text-[#6c757d] truncate">{group.name}</div>
          <div className="text-[10px] text-[#6c757d] font-bold mt-0.5">
            {meta.wins || 0}W · {meta.places || 0}P · {meta.unplaced || 0}U
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-2xl text-[#212529] tabular-nums" data-testid="my-total-points">
            {(meta.total_points || 0).toFixed(2)}
          </div>
          <div className="text-[9px] font-black uppercase tracking-wider text-[#6c757d]">pts · rank {meta.rank || "—"}</div>
          <div className="text-[9px] font-black uppercase tracking-wider text-[#6741d9] mt-1" data-testid="wildcards-remaining">
            ⚡ {meta.wildcards_remaining}/{meta.wildcards_allowance}
          </div>
        </div>
      </div>

      {/* Date chips */}
      {dates.length > 0 && (
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4">
          {dates.map((d) => {
            const active = d.meeting_date === activeDate;
            return (
              <button
                key={d.meeting_date}
                onClick={() => setActiveDate(d.meeting_date)}
                data-testid={`mypicks-date-${d.meeting_date}`}
                className={`shrink-0 px-3 py-1.5 rounded-full font-bold text-xs border transition-transform active:scale-95 ${
                  active
                    ? "bg-[#1971c2] text-white border-[#1971c2]"
                    : "bg-white text-[#212529] border-[#e9ecef]"
                }`}
              >
                {d.meeting_date} · {d.race_count}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <Skeleton rows={4} height={80} testId="mypicks-skeleton" />
      ) : races.length === 0 ? (
        <div className="bg-white border border-[#e9ecef] rounded-2xl p-8 text-center" data-testid="mypicks-empty">
          <div className="text-5xl mb-3">🎟️</div>
          <div className="font-display font-extrabold text-xl">Nothing here yet</div>
          <p className="text-sm text-[#6c757d] mt-2">Load a race card to start picking.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-picks-list">
          {races.map((r) => {
            const pick = picks.find((p) => p.race_id === r.id);
            const horse = pick ? r.horses.find((h) => h.horse_number === pick.horse_number) : null;
            const originalHorse = pick && pick.original_horse_number
              ? r.horses.find((h) => h.horse_number === pick.original_horse_number)
              : null;
            const finished = r.status === "finished";
            const silk = horse ? SILK_COLOURS[(horse.horse_number - 1) % 12] : null;
            return (
              <div key={r.id} className="bg-white border border-[#e9ecef] rounded-2xl p-4"
                   data-testid={`my-pick-row-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-[10px] font-bold uppercase tracking-wider text-[#6c757d]">
                  <span>{r.track} · R{r.race_number}</span>
                  <span>·</span>
                  <span>{r.race_name}</span>
                </div>
                {pick && horse ? (
                  <div className="mt-2 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-md flex items-center justify-center text-white font-black text-sm shrink-0"
                         style={{ backgroundColor: silk }}>
                      {horse.horse_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-extrabold text-base truncate">{horse.name}</div>
                      <div className="text-[11px] text-[#6c757d] truncate">
                        Bar {horse.barrier} · {horse.jockey} · @ {horse.odds.toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleWildcard(pick)}
                      disabled={finished || (!pick.wildcard_applied && meta.wildcards_remaining <= 0)}
                      data-testid={`wildcard-toggle-${r.id}`}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold border transition-colors active:scale-95 flex items-center gap-1 ${
                        pick.wildcard_applied
                          ? "bg-[#6741d9] text-white border-[#6741d9]"
                          : "bg-white text-[#6741d9] border-[#6741d9]/40 hover:border-[#6741d9]"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                      {pick.wildcard_applied ? "Wildcard on" : "Wildcard"}
                    </button>
                    <span className="text-[9px] font-black uppercase tracking-wider bg-[#f8f9fa] border border-[#e9ecef] rounded-full px-1.5 py-0.5 text-[#6c757d]">
                      {pick.status}
                    </span>
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-[#6c757d] italic">No pick on this race yet.</div>
                )}
                {pick && finished && r.result && <ScoreBreakdown pick={pick} race={r} />}
                {pick && pick.scratching_replacement && originalHorse && (
                  <div className="mt-3 flex items-start gap-2 bg-[#fff4e6] border border-[#c07800]/25 rounded-xl p-3 text-xs text-[#c07800] font-semibold"
                       data-testid={`scratch-notice-${r.id}`}>
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>
                      Your original pick <b>{originalHorse.name}</b> was scratched. Auto-swapped to favourite <b>{horse?.name}</b>.
                    </span>
                  </div>
                )}
                {finished && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#6c757d]">
                    <Lock className="w-3 h-3" strokeWidth={2.5} /> Race finished — wildcard locked
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
